import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCourseIndex, useCourseManifest } from "@/lib/course";
import type { ChapterManifestEntry, CourseManifest } from "@/lib/course";
import { useProgress, resumeLocation, lessonId } from "@/lib/progress";
import type { ProgressState } from "@/lib/progress";
import {
  selectResumeTarget,
  courseMinutesRemaining,
  chapterMinutesRemaining,
  formatMinutes
} from "@/lib/resume";
import type { CourseResumeInfo, CourseStatus, ResumeTarget } from "@/lib/resume";

// Home's "Pick up where you left off" hero. Renders above the catalog rows
// (and above CoachNotesCard) so the single most relevant next action is the
// first thing a returning learner sees, per the 2026-07-06 UI/LMS review
// (finding 1.1). The catalog rows below are unchanged — this is additive.

// Mirrors CourseCard.tsx's (unexported) courseStatus so the hero and the
// catalog rows never disagree about what counts as "in progress".
function courseStatus(progress: ProgressState, totalLessons: number): CourseStatus {
  if (progress.status !== "ready") return "not-started";
  const completed = progress.progress.completedLessons.length;
  const visited = progress.progress.lastVisited !== null;
  if (totalLessons > 0 && completed >= totalLessons) return "completed";
  if (completed === 0 && !visited) return "not-started";
  return "in-progress";
}

interface ResumeLoc {
  chapter: string;
  lesson: string;
}

// Mirrors CourseCard.tsx:113-124's nextIncompleteLoc — the first lesson in
// course order whose lessonId isn't in the completed set.
function firstIncompleteLesson(manifest: CourseManifest, completed: Set<string>): ResumeLoc | null {
  for (const ch of manifest.chapters) {
    for (const ls of ch.lessons) {
      if (!completed.has(lessonId(ch.folder, ls.slug))) {
        return { chapter: ch.folder, lesson: ls.slug };
      }
    }
  }
  return null;
}

// Per-course data a CourseProbe reports once its manifest + progress are both
// ready. Keyed by slug in the parent's `byslug` map; HeroPanel reads whichever
// entry `selectResumeTarget` picked. All fields are required (never `?:`) so
// there's no exactOptionalPropertyTypes friction — absence is modeled with
// `null`/`""`/`0`, not a missing key.
export interface ProbePayload {
  slug: string;
  title: string;
  status: CourseStatus;
  lastVisitedTs: number | null;
  indexOrder: number;
  resume: ResumeLoc | null;
  resumeChapterNumber: number;
  resumeChapterTitle: string;
  resumeLessonTitle: string;
  // Total estimated minutes for the resume chapter (not just what's left) —
  // lets HeroPanel draw a percent-complete bar without re-fetching the manifest.
  resumeChapterTotalMins: number;
  courseMins: number;
  chapterMins: number;
}

// What a CourseProbe reports up when it reaches a terminal state. A course
// whose manifest/progress fetch errors reports the `errored` sentinel so the
// parent can count it toward "all resolved" (one bad course can't stall the
// gate) while excluding it from resume-target selection — mirroring how
// CourseCardForSlug degrades per-card.
type ProbeResult =
  | { errored: false; payload: ProbePayload }
  | { errored: true; slug: string; indexOrder: number };

interface CourseProbeProps {
  slug: string;
  title: string;
  indexOrder: number;
  onResolved: (result: ProbeResult) => void;
}

// Renders nothing. Exists purely to own one course's useCourseManifest +
// useProgress hook pair: calling those hooks in a loop inside ResumeHero
// (whose list length varies with the course index) would violate
// rules-of-hooks, so each course gets its own component instance with a
// fixed, stable hook count instead. Reports once via onResolved when it
// reaches a terminal state — either both hooks "ready" (success) or either
// "error" (degrade); the parent's setState guard makes repeat/StrictMode
// re-invocations inert.
function CourseProbe({ slug, title, indexOrder, onResolved }: CourseProbeProps): null {
  const manifest = useCourseManifest(slug);
  const progress = useProgress(manifest.status === "ready" ? manifest.manifest.slug : "");

  useEffect(() => {
    // Either fetch failing is terminal — report the course as errored so it's
    // dropped from selection but still counts toward the resolved gate.
    if (manifest.status === "error" || progress.status === "error") {
      onResolved({ errored: true, slug, indexOrder });
      return;
    }
    if (manifest.status !== "ready" || progress.status !== "ready") return;
    const m = manifest.manifest;
    const totalLessons = m.chapters.reduce((a, c) => a + c.lessons.length, 0);
    const status = courseStatus(progress, totalLessons);
    const completed = new Set(progress.progress.completedLessons);

    const nextIncompleteLoc = firstIncompleteLesson(m, completed);
    const resume = resumeLocation(progress.progress, nextIncompleteLoc);
    const resumeCh: ChapterManifestEntry | undefined = resume
      ? m.chapters.find((c) => c.folder === resume.chapter)
      : undefined;
    const resumeLessonTitle = resume
      ? (resumeCh?.lessons.find((l) => l.slug === resume.lesson)?.title ?? "")
      : "";

    onResolved({
      errored: false,
      payload: {
        slug,
        title,
        status,
        lastVisitedTs: progress.progress.lastVisited?.ts ?? null,
        indexOrder,
        resume,
        resumeChapterNumber: resumeCh?.chapterNumber ?? 0,
        resumeChapterTitle: resumeCh?.title ?? "",
        resumeLessonTitle,
        resumeChapterTotalMins: resumeCh
          ? resumeCh.lessons.reduce((a, l) => a + l.estimatedMinutes, 0)
          : 0,
        courseMins: courseMinutesRemaining(m, completed),
        chapterMins: resumeCh ? chapterMinutesRemaining(resumeCh, completed) : 0
      }
    });
    // slug/title/indexOrder/onResolved are stable for a given course's probe
    // instance; only the two status-flips matter for re-running this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest.status, progress.status]);

  return null;
}

export function ResumeHero(): JSX.Element | null {
  const index = useCourseIndex();
  const [resolved, setResolved] = useState<Record<string, ProbeResult>>({});

  if (index.status !== "ready" || index.courses.length === 0) return null;

  // Ready payloads only — errored courses are dropped here so they never reach
  // selectResumeTarget or HeroPanel.
  const readyPayloads: Record<string, ProbePayload> = {};
  for (const r of Object.values(resolved)) {
    if (!r.errored) readyPayloads[r.payload.slug] = r.payload;
  }
  const infos = Object.values(readyPayloads).map(
    (p): CourseResumeInfo => ({
      slug: p.slug,
      status: p.status,
      lastVisitedTs: p.lastVisitedTs,
      indexOrder: p.indexOrder
    })
  );
  // Errored probes count toward the gate too — one bad course can't stall the
  // hero. All courses erroring → infos is empty → selectResumeTarget returns
  // { kind: "none" } → hero renders nothing.
  const allResolved = Object.keys(resolved).length === index.courses.length;
  const target = allResolved ? selectResumeTarget(infos) : null;

  return (
    <>
      {index.courses.map((c, i) => (
        <CourseProbe
          key={c.slug}
          slug={c.slug}
          title={c.title}
          indexOrder={i}
          onResolved={(r) => setResolved((m) => (m[c.slug] ? m : { ...m, [c.slug]: r }))}
        />
      ))}
      {target && <HeroPanel target={target} data={readyPayloads} />}
    </>
  );
}

interface HeroPanelProps {
  target: ResumeTarget;
  data: Record<string, ProbePayload>;
}

function HeroPanel({ target, data }: HeroPanelProps): JSX.Element | null {
  if (target.kind === "none") return null;

  if (target.kind === "all-complete") {
    return (
      <section className="mb-8 rounded-card border border-border bg-card shadow-card p-6">
        <p className="text-muted-foreground text-sm font-medium">You&rsquo;re all caught up 🎓</p>
      </section>
    );
  }

  const info = data[target.slug];
  // Defensive only: selectResumeTarget can only name a slug whose probe has
  // already resolved, so `data[target.slug]` is always present in practice.
  if (!info) return null;

  const href = info.resume
    ? `/courses/${info.slug}/${info.resume.chapter}/${info.resume.lesson}`
    : `/courses/${info.slug}`;

  if (target.kind === "start") {
    return (
      <section className="mb-8 rounded-card border border-border bg-card shadow-card p-6">
        <h2 className="font-display text-xl font-extrabold tracking-tight mb-1">
          Start {info.title}
        </h2>
        <p className="text-muted-foreground text-sm mb-4">{formatMinutes(info.courseMins)} total</p>
        <Link
          to={href}
          className="inline-flex items-center rounded-control bg-primary text-primary-foreground px-5 py-3 text-base font-bold hover:bg-primary-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Start course →
        </Link>
      </section>
    );
  }

  // target.kind === "resume"
  const percentDone =
    info.resumeChapterTotalMins > 0
      ? Math.round(
          ((info.resumeChapterTotalMins - info.chapterMins) / info.resumeChapterTotalMins) * 100
        )
      : 0;

  return (
    <section className="mb-8 rounded-card border border-border bg-card shadow-card p-6">
      <h2 className="font-display text-xl font-extrabold tracking-tight mb-1">
        Pick up where you left off
      </h2>
      <p className="text-foreground font-semibold mb-0.5">{info.title}</p>
      <p className="text-muted-foreground text-sm mb-3">
        Chapter {info.resumeChapterNumber}: {info.resumeChapterTitle} · {info.resumeLessonTitle}
      </p>
      <div className="h-1.5 w-full max-w-xs rounded-full bg-muted overflow-hidden mb-3" aria-hidden="true">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.min(100, Math.max(0, percentDone))}%` }}
        />
      </div>
      <p className="text-muted-foreground text-xs mb-4">
        {formatMinutes(info.chapterMins)} left in this chapter · {formatMinutes(info.courseMins)} in the course
      </p>
      <Link
        to={href}
        className="inline-flex items-center rounded-control bg-primary text-primary-foreground px-5 py-3 text-base font-bold hover:bg-primary-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        Continue →
      </Link>
    </section>
  );
}
